import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import { Sync as SyncIcon, DirectionsRun as RunIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api.service';
import { userService } from '../../services/user.service';
import { WorkoutActivity } from '../../models/types';
import { PageHeader } from '../ui/PageHeader';
import { SegmentedControl } from '../ui/SegmentedControl';
import { SurfaceCard } from '../ui/SurfaceCard';
import { EmptyState } from '../ui/EmptyState';
import { ActivityRow } from './ActivityRow';
import { ActivityKind, activityKindLabel, activityKindOf, formatDuration, sumDistance, sumTime } from './activityHelpers';
import { formatDate } from '../../i18n/locale-format';

type Filter = 'All' | Exclude<ActivityKind, 'Other'>;
const FILTER_VALUES: Filter[] = ['All', 'Run', 'Ride', 'Swim'];

const MonthStat = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ backgroundColor: 'var(--card)', p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
    <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{label}</Box>
    <Box component="span" sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
  </Box>
);

export const ActivitiesList = () => {
  const { t } = useTranslation('activities');
  const [activities, setActivities] = useState<WorkoutActivity[]>([]);
  const [loading, setLoading] = useState(true);
  // null = no error; '' = failed without a server message (generic text shown at render time).
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('All');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const fetchActivities = useCallback(async () => {
    try {
      setActivities(await apiService.getUserActivities());
      setError(null);
    } catch (err: any) { // fetch errors are untyped; only .message is read
      setError(err.message || '');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchActivities().finally(() => setLoading(false));
  }, [fetchActivities]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await userService.syncStravaActivities();
      await fetchActivities();
      setSyncMessage({ severity: 'success', text: t('list.synced', { count: res.data.syncedCount }) });
    } catch (err: any) { // axios error shape varies; fall back to a generic message
      setSyncMessage({ severity: 'error', text: err?.response?.data?.message || err?.message || t('list.syncFailed') });
    } finally {
      setSyncing(false);
    }
  };

  const sorted = useMemo(
    () => [...activities].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
    [activities]
  );
  const visible = useMemo(() => (filter === 'All' ? sorted : sorted.filter(a => activityKindOf(a.workoutType) === filter)), [sorted, filter]);

  const now = new Date();
  const monthItems = useMemo(
    () => activities.filter(a => {
      const d = new Date(a.startDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities]
  );
  const monthName = formatDate(now, { month: 'long' });
  const filters = FILTER_VALUES.map(value => ({ value, label: value === 'All' ? t('list.filterAll') : activityKindLabel(value) }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        actions={
          <Button variant="outlined" startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />} onClick={handleSync} disabled={syncing}>
            {t('list.syncNow')}
          </Button>
        }
      />

      {error !== null && <Alert severity="error">{error || t('list.fetchFailed')}</Alert>}
      {syncMessage && <Alert severity={syncMessage.severity} onClose={() => setSyncMessage(null)}>{syncMessage.text}</Alert>}

      {error === null && activities.length === 0 ? (
        <SurfaceCard>
          <EmptyState
            icon={<RunIcon />}
            title={t('list.emptyTitle')}
            description={t('list.emptyDescription')}
          />
        </SurfaceCard>
      ) : (
        <>
          <Box sx={{ display: 'grid', gap: '1px', backgroundColor: 'var(--border)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
            <MonthStat label={t('month.distance', { month: monthName })} value={`${sumDistance(monthItems).toFixed(1)} km`} />
            <MonthStat label={t('month.activities')} value={String(monthItems.length)} />
            <MonthStat label={t('month.movingTime')} value={formatDuration(sumTime(monthItems))} />
          </Box>

          <SegmentedControl options={filters} value={filter} onChange={setFilter} ariaLabel={t('list.filterAriaLabel')} />

          <SurfaceCard bodyPadding={0} sx={{ overflow: 'hidden' }}>
            {visible.length === 0 ? (
              <EmptyState title={filter === 'All' ? t('list.emptyTitle') : t('list.filterEmptyTitle', { kind: activityKindLabel(filter).toLowerCase() })} description={t('list.filterEmptyDescription')} />
            ) : (
              visible.map(a => <ActivityRow key={a._id} activity={a} />)
            )}
          </SurfaceCard>
        </>
      )}
    </>
  );
};
